// src/pages/LegalPage.jsx
//
// WP-3 public /legal page. Renders the latest published version of each
// /legalDocuments type's contentMarkdown (locked #7: content in Firestore, NOT
// Storage). NOT in the prototype -> built by extending the design language
// (CLAUDE.md section 1.7): gold/book theme content cards, font-display headings.
//
// Optional deep-link: /legal#privacy_policy scrolls to that section.
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useLegalDocs, LEGAL_TYPE_LABELS, LEGAL_TYPE_ORDER } from '../storage/useLegalDocs.js';
import { AUTH_MODE } from '../auth/firebase.js';

function fmtDate(v) {
    if (!v) return '';
    const d = typeof v.toDate === 'function' ? v.toDate() : new Date(v);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

export default function LegalPage() {
    const { hash } = useLocation();
    const { byType, loading } = useLegalDocs();

    useEffect(() => {
        if (!loading && hash) {
            const el = document.getElementById(hash.replace('#', ''));
            if (el) el.scrollIntoView({ behavior: 'smooth' });
        }
    }, [loading, hash]);

    const ordered = LEGAL_TYPE_ORDER.map((ty) => byType[ty]).filter(Boolean);

    return (
        <div className="page-fade-in min-h-[calc(100vh-4rem)] bg-book-bg paper-texture">
            <div className="max-w-3xl mx-auto px-4 md:px-8 py-10 md:py-16">
                <div className="mb-10">
                    <div className="font-display text-book-accent text-xs mb-2 font-semibold">Legal</div>
                    <h1 className="font-display text-3xl md:text-5xl text-book-text">Policies &amp; Terms</h1>
                </div>

                {loading && (
                    <p className="font-serif italic text-book-muted">Loading legal documents…</p>
                )}

                {!loading && ordered.length === 0 && (
                    <div className="border border-book-accent/30 bg-book-page/40 p-8">
                        <p className="font-serif italic text-book-muted">
                            {AUTH_MODE === 'mock'
                                ? 'Legal documents load from Firestore and are not available in mock mode.'
                                : 'Legal documents have not been published yet.'}
                        </p>
                    </div>
                )}

                <section id="analytics" className="border border-book-accent/30 bg-book-page/40 p-6 mb-8 font-serif text-book-text space-y-3">
                    <h2 className="font-display text-book-accent text-xl">Optional website analytics</h2>
                    <button className="border border-book-line rounded-full px-4 py-2 text-sm" onClick={() => window.dispatchEvent(new Event('btl-cookie-choices'))}>Cookie choices</button>
                    <p>With your permission, we use Google Analytics to understand visits, traffic sources, device types, approximate regions and actions such as registrations and purchases. Google processes this usage data and a browser cookie identifier; data may be processed outside Hong Kong.</p>
                    <p>We do not send your name, email, account ID, essays, chat messages or form contents. Purchase reporting uses an opaque order identifier, pack identifiers, currency and amount. It is separate from the records needed to fulfil your order.</p>
                    <p>Analytics is off until you choose Allow analytics. Choose Essential only to continue without it, or use Cookie choices to withdraw permission. Your choice is remembered for up to six months. Google Analytics cookies are configured for up to six months. Withdrawing permission stops future collection and removes these cookies from this browser; it does not erase previously collected reports.</p>
                    <p>Analytics + ad measurement is a separate optional choice. It permits Google Analytics and Google Ads to connect advertising clicks to purchases using a Google click identifier, browser identifiers and the purchase fields described above. Google may store first-party advertising measurement cookies. Personalised advertising, remarketing audiences and Google Signals remain off. Existing analytics permission does not include this choice. You can switch back to analytics only or essential only at any time; we remove advertising measurement cookies from this browser. No advertising click identifiers are kept in a separate BTL database or browser store.</p>
                    <p>Google's <a className="underline" href="https://policies.google.com/privacy">privacy policy</a> explains its processing. Contact learn@daniellenglish.com with privacy questions.</p>
                </section>
                <div className="space-y-8">
                    {ordered.map((doc) => (
                        <section key={doc.type} id={doc.type}
                            className="border border-book-accent/30 bg-book-page/40 shadow-book p-6 md:p-8">
                            <div className="flex items-baseline justify-between gap-4 mb-4 pb-4 border-b border-book-accent/20">
                                <h2 className="font-display text-book-accent text-xl md:text-2xl">
                                    {LEGAL_TYPE_LABELS[doc.type] || doc.type}
                                </h2>
                                <span className="font-display text-book-muted text-xs whitespace-nowrap font-semibold">
                                    {doc.version}{fmtDate(doc.publishedAt) ? ` · ${fmtDate(doc.publishedAt)}` : ''}
                                </span>
                            </div>
                            <div className="legal-markdown font-serif text-book-text leading-relaxed space-y-3">
                                <ReactMarkdown>{doc.contentMarkdown || ''}</ReactMarkdown>
                            </div>
                        </section>
                    ))}
                </div>
            </div>
        </div>
    );
}
